import axios from 'axios';
import moment from 'moment';

const host = process.env.VUE_APP_API_HOST;
const clientID = process.env.VUE_APP_API_CLIENT_ID;
const redirectUri = process.env.VUE_APP_API_REDIRECT_URI;

const moduleAuth = {
    namespaced: true,
    state: () => ({
        authToken: window.localStorage.getItem('authToken'),
        refreshToken: window.localStorage.getItem('refreshToken'),
        expiresAt: window.localStorage.getItem('expiresAt'),
        verifier: window.localStorage.getItem('verifier'),
        heimbasServer: window.localStorage.getItem('heimbasServer'),
        jwt: window.localStorage.getItem('jwt'),
        jwtExpiresAt: window.localStorage.getItem('jwtExpiresAt'),
        jwtCertificate: window.localStorage.getItem('jwtCertificate'),
    }),
    getters: {
        getAuthToken: (state) => {
            return state.authToken
        },
        getRefreshToken: (state) => {
            return state.refreshToken
        },
        getExpiresAt: (state) => {
            return state.expiresAt
        },
        verifier: (state) => {
            return state.verifier
        },
        heimbasServer: (state) => {
            return state.heimbasServer
        },
        jwt: (state) => {
            return state.jwt
        },
        jwtExpiresAt: (state) => {
            return state.jwtExpiresAt
        },
        jwtCertificate: (state) => {
            return state.jwtCertificate
        }
    },
    mutations: {
        /**
         * Set the state authToken
         * @param {object} state The state of the Vuex Store
         * @param {string} authToken The new value for authToken
         */
        setAuthToken (state, {authToken}) {
            window.localStorage.setItem('authToken', authToken);
            state.authToken = authToken;
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + authToken;
        },
        /**
         * Set the state refreshToken
         * @param {object} state The state of the Vuex Store
         * @param {string} refreshToken The new value for refreshToken
         */
        setRefreshToken (state, {refreshToken}) {
            window.localStorage.setItem('refreshToken', refreshToken);
            state.refreshToken = refreshToken;
        },
        /**
         * Set the state expiresAt
         * @param {object} state The state of the Vuex Store
         * @param {string} expiresAt The new value for expiresAt
         */
        setExpiresAt (state, {expiresAt}) {
            window.localStorage.setItem('expiresAt', expiresAt);
            state.expiresAt = expiresAt;
        },
        /**
         * Set the state verifier
         * @param {object} state The state of the Vuex Store
         * @param {string} verifier The new value for verifier
         */
        setVerifier (state, {verifier}) {
            window.localStorage.setItem('verifier', verifier);
            state.verifier = verifier;
        },
        /**
         * Set the state heimbasServer
         * @param {object} state The state of the Vuex Store
         * @param {string} heimbasServer The new value for heimbasServer
         */
        setHeimbasServer (state, {heimbasServer}) {
            window.localStorage.setItem('heimbasServer', heimbasServer);
            state.heimbasServer = heimbasServer;
        },
        /**
         * Set the state jwt
         * @param {object} state The state of the Vuex Store
         * @param {string} jwt The new value for jwt
         */
        setJwt (state, {jwt}) {
            window.localStorage.setItem('jwt', jwt);
            state.jwt = jwt;
        },
        /**
         * Set the state jwtExpiresAt
         * @param {object} state The state of the Vuex Store
         * @param {string} jwtExpiresAt The new value for jwtExpiresAt
         */
        setJwtExpiresAt (state, {jwtExpiresAt}) {
            window.localStorage.setItem('jwtExpiresAt', jwtExpiresAt);
            state.jwtExpiresAt = jwtExpiresAt;
        },
        /**
         * Set the state jwtCertificate
         * @param {object} state The state of the Vuex Store
         * @param {string} jwtCertificate The new value for jwtCertificate
         */
         setJwtCertificate (state, {jwtCertificate}) {
            window.localStorage.setItem('jwtCertificate', jwtCertificate);
            state.jwtCertificate = jwtCertificate;
        },
    },
    actions: {
        /**
         * Calls the get token route and stores the response
         * @param {object} context Exposes the same set of methods/properties on the store instance
         * @return {Promise<object>}
         */
        loginGetOAuthToken (context, {code}) {
            return new Promise((resolve, reject) => {
                let url = host + '/oauth/token';

                let requestData = {
                    redirect_uri: redirectUri,
                    client_id: parseInt(clientID),
                    grant_type: 'authorization_code',
                    code_verifier: context.state.verifier,
                    code: code,
                    client_secret: ''
                };

                axios.post(url, requestData ).then((response) => {
                    context.commit('setAuthToken', {'authToken': response.data.access_token});
                    context.commit('setRefreshToken', {'refreshToken': response.data.refresh_token});
                    let expiresAt = moment(moment.now()).add(response.data.expires_in, 'seconds');
                    context.commit('setExpiresAt', {'expiresAt': expiresAt.format("ddd, DD MMM YYYY HH:mm:ss ZZ")});
                    context.commit('setVerifier', {'verifier': null});
                    resolve(response);
                }).catch((error) => {
                    reject(error);
                });
            });
        },
        /**
         * Calls the token refresh route and stores the response
         * @param {object} context Exposes the same set of methods/properties on the store instance
         * @return {Promise<object>}
         */
        refreshOAuthToken (context) {
            return new Promise((resolve, reject) => {
                let url = host + '/oauth/token';
                let requestData = {
                    headers: {
                        Authorization: 'Bearer ' + context.state.authToken
                    },
                    grant_type: 'refresh_token',
                    client_id: clientID,
                    refresh_token: context.state.refreshToken,
                    client_secret: ''
                };
                axios.post(url, requestData).then((response) => {
                    context.commit('setAuthToken', {'authToken': response.data.access_token});
                    context.commit('setRefreshToken', {'refreshToken': response.data.refresh_token});
                    let expiresAt = moment(moment.now()).add(response.data.expires_in, 'seconds');
                    context.commit('setExpiresAt', {'expiresAt': expiresAt});
                    resolve(response);
                }).catch((error) => {
                    reject(error);
                });
            });
        },
        
        /**
         * Always returns a valid JWT
         * @param {object} context Exposes the same set of methods/properties on the store instance
         * @return {Promise<object>}
         */
        getValidJWT (context) {
            return new Promise((resolve,reject) => {
                let expiresAt = context.state.jwtExpiresAt;
                let now = moment(moment.now());
                if(now.add(5, 'm').isBefore(expiresAt)){
                    resolve(context.state.jwt)
                } else {
                    let url = host + '/api/v1/jwt';
                    axios.post(url).then((response) => {
                        context.commit('setJwt', {'jwt': response.data.token});
                        context.commit('setHeimbasServer', {'heimbasServer': response.data.server});
                        let expiresAt = moment.unix(response.data.valid_until);
                        context.commit('setJwtExpiresAt', {'jwtExpiresAt': expiresAt.format("ddd, DD MMM YYYY HH:mm:ss ZZ")});
                        context.commit('setJwtCertificate', {'jwtCertificate': response.data.certificate});
                        resolve(response.data.token);
                    }).catch((error) => {
                        reject(error);
                    });
                }
            });
        },
    }
}
export default moduleAuth;